import React, { useState } from 'react';
import { Card, Typography, Button, Space, Divider } from 'antd';
import { 
  ThunderboltOutlined, 
  SafetyCertificateOutlined, 
  RocketOutlined,
  WifiOutlined,
  SendOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  UpOutlined,
  DownOutlined,
  TeamOutlined,
  FileSearchOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const InfoInstruction = () => {
  const [collapsed, setCollapsed] = useState(false);

  const features = [
    {
      icon: <ThunderboltOutlined style={{ color: '#faad14' }} />,
      title: 'Lightning Fast',
      description: 'Direct device-to-device transfers at maximum speed'
    },
    {
      icon: <SafetyCertificateOutlined style={{ color: '#52c41a' }} />,
      title: 'Privacy First',
      description: 'No cloud storage, no tracking, completely private'
    },
    {
      icon: <WifiOutlined style={{ color: '#1890ff' }} />,
      title: 'Local Network',
      description: 'Works on any Wi-Fi network without internet'
    }
  ];

  const steps = [
    {
      icon: <WifiOutlined />,
      text: 'Open this site in your browser'
    },
    {
      icon: <TeamOutlined />,
      text: 'See Connected Users on the same Local/WiFi network'
    },
    {
      icon: <SendOutlined />,
      text: 'Choose User and click Send file button'
    },
    {
      icon: <FileSearchOutlined />,
      text: 'Choose File'
    },
    {
      icon: <DownloadOutlined />,
      text: 'On Receiver side, click Accept button to start receiving'
    }
  ];

  return (
    <Card 
      style={{ marginBottom: 16, marginTop: 16 }}
      bodyStyle={{ padding: collapsed ? '16px 24px' : '24px' }}
    >
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Title level={3} style={{ margin: 0, color: '#1890ff', flex: 1 }}>
            <ThunderboltOutlined /> Instant, Private File Sharing — Directly Between Devices
          </Title>
        </div>
        
        <div style={{ textAlign: 'right', marginBottom: collapsed ? 0 : 16 }}>
          <Button 
            type="text" 
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            size="small"
          >
            {collapsed ? 'Show' : 'Hide'}
          </Button>
        </div>
        
        {!collapsed && (
          <>
            <Paragraph style={{ marginTop: 0, fontSize: '16px', lineHeight: 1.6 }}>
              Send and receive files instantly with anyone on your local network — no cloud, no third parties, no uploads. 
              Using cutting-edge <Text strong>WebRTC technology</Text>, our tool enables true peer-to-peer file sharing right from your browser.
            </Paragraph>

            <Paragraph style={{ fontSize: '16px', lineHeight: 1.6 }}>
              Whether you're sharing photos, documents, videos, or projects, just connect to the same Wi-Fi network 
              and transfer files at lightning speed — directly between devices.
            </Paragraph>

            <Divider />

            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ color: '#faad14' }}>
                ✨ How it works:
              </Title>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {steps.map((step, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    gap: 12,
                    minHeight: 32
                  }}>
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      minWidth: 32,
                      borderRadius: '50%', 
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1890ff',
                      flexShrink: 0
                    }}>
                      {step.icon}
                    </div>
                    <Text style={{ 
                      fontSize: '15px',
                      flex: 1,
                      lineHeight: '1.5'
                    }}>
                      {step.text}
                    </Text>
                  </div>
                ))}
              </Space>
            </div>

            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ color: '#52c41a' }}>
                🔒 Privacy by design:
              </Title>
              <Paragraph style={{ fontSize: '15px', paddingLeft: 16 }}>
                <EyeInvisibleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                Your files never leave your network. We don't store your data — 
                it's all private, secure, and temporary.
              </Paragraph>
            </div>
          </>
        )}

        {collapsed && (
          <div style={{ marginTop: 12 }}>
            <Space size="large" wrap>
              {features.map((feature, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {feature.icon}
                  <Text strong>{feature.title}</Text>
                </div>
              ))}
            </Space>
          </div>
        )}
      </div>
    </Card>
  );
};

export default InfoInstruction;